/*
  <customer-support-chat>: the Customer Support page's Chat button.

  Asks the Zendesk chat gate (snippets/consentmo-zendesk.liquid) to open the chat window
  with a dispatch:zendesk-chat:open event, then reacts to the gate's answer:
  - on:zendesk-chat:opened: hide any message.
  - on:zendesk-chat:blocked: the visitor has not allowed functional cookies, which chat
    needs. Show a message and, when Consentmo's documented openDialog() is available, a
    button that opens their cookie preferences.
  - on:zendesk-chat:failed, or no answer at all: show the "not available" message.

  It only reacts while waiting for its own request, and it never moves focus. Messages
  are revealed inside a role="status" region that is in the page from the start, so
  screen readers announce them.
*/
if (!customElements.get('customer-support-chat')) {
  customElements.define(
    'customer-support-chat',
    class CustomerSupportChat extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-chat-open]');
        this.preferencesButton = this.querySelector('[data-chat-preferences]');
        this.messages = {
          opening: this.querySelector('[data-chat-message="opening"]'),
          blocked: this.querySelector('[data-chat-message="blocked"]'),
          failed: this.querySelector('[data-chat-message="failed"]'),
        };
        this.waiting = false;

        this.handleClick = this.handleClick.bind(this);
        this.handleOpened = this.handleOpened.bind(this);
        this.handleBlocked = this.show.bind(this, 'blocked');
        this.handleFailed = this.show.bind(this, 'failed');
        this.handlePreferences = this.handlePreferences.bind(this);

        if (this.button) this.button.addEventListener('click', this.handleClick);
        if (this.preferencesButton) this.preferencesButton.addEventListener('click', this.handlePreferences);
        document.addEventListener('on:zendesk-chat:opened', this.handleOpened);
        document.addEventListener('on:zendesk-chat:blocked', this.handleBlocked);
        document.addEventListener('on:zendesk-chat:failed', this.handleFailed);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.handleClick);
        if (this.preferencesButton) this.preferencesButton.removeEventListener('click', this.handlePreferences);
        document.removeEventListener('on:zendesk-chat:opened', this.handleOpened);
        document.removeEventListener('on:zendesk-chat:blocked', this.handleBlocked);
        document.removeEventListener('on:zendesk-chat:failed', this.handleFailed);
        this.clearTimers();
      }

      handleClick() {
        this.hideAll();
        this.clearTimers();
        this.waiting = true;
        this.awaitingConsent = false;

        // Only say "Opening chat..." if it is not instant, so a quick open stays quiet.
        this.openingTimer = setTimeout(() => {
          if (this.waiting) this.reveal('opening');
        }, 800);

        // If nothing answers (for example, the chat gate is not on this page), say so.
        this.noAnswerTimer = setTimeout(() => {
          if (this.waiting) this.show('failed');
        }, 12000);

        document.dispatchEvent(
          new CustomEvent('dispatch:zendesk-chat:open', { detail: { opener: this.button } }),
        );
      }

      handleOpened() {
        // If focus is on the cookie preferences button, which is about to hide, move it
        // back to Chat Now, the same place the gate returns focus to when chat closes.
        if (this.preferencesButton && this.preferencesButton === document.activeElement && this.button) {
          this.button.focus();
        }
        this.waiting = false;
        this.awaitingConsent = false;
        this.clearTimers();
        this.hideAll();
      }

      handlePreferences() {
        if (this.canOpenPreferences()) {
          window.Consentmo.openDialog('preferences');
        }
      }

      canOpenPreferences() {
        return Boolean(window.Consentmo && typeof window.Consentmo.openDialog === 'function');
      }

      show(name) {
        // After "blocked", a "failed" still matters: the visitor may have allowed chat and
        // Zendesk then did not load, so the cookie message would no longer be true.
        const failedAfterConsent = name === 'failed' && this.awaitingConsent;
        if (!this.waiting && !failedAfterConsent) return;
        this.waiting = false;
        this.awaitingConsent = name === 'blocked';
        this.clearTimers();
        this.reveal(name);
      }

      reveal(name) {
        this.hideAll();
        clearTimeout(this.revealTimer);

        // Reveal on a later tick, so pressing the button again re-announces the same
        // message instead of leaving the live region unchanged.
        this.revealTimer = setTimeout(() => {
          if (this.messages[name]) this.messages[name].hidden = false;

          // Consentmo loads as an app embed, so check for its API when the message shows.
          if (name === 'blocked' && this.preferencesButton) {
            this.preferencesButton.hidden = !this.canOpenPreferences();
          }
        }, 100);
      }

      hideAll() {
        Object.values(this.messages).forEach((message) => {
          if (message) message.hidden = true;
        });
        if (this.preferencesButton) this.preferencesButton.hidden = true;
      }

      clearTimers() {
        clearTimeout(this.openingTimer);
        clearTimeout(this.noAnswerTimer);
        clearTimeout(this.revealTimer);
      }
    },
  );
}
